import type { CSSProperties } from 'react';
import { toast, ToastContainer } from 'react-toastify';
// react-toastify のベーススタイル（position: fixed やアニメーション等）。
// これが無いとトーストが不可視になるため、ライブラリ側で明示的に読み込む。
import 'react-toastify/dist/ReactToastify.css';
import { MaterialIcon } from '@/components/MaterialIcon';
import './toast.scss';

type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading';

const CustomCloseButton = ({ closeToast }: { closeToast: () => void }) => (
  <button
    type="button"
    onClick={closeToast}
    className="ml-auto flex size-xl items-center justify-center rounded border border-solid border-border bg-white"
  >
    <img src="/agent/job_change_processes/icon-close.svg" alt="閉じる" width={16} height={16} />
  </button>
);

// トーストの共通スタイル（複数行対応: 高さは内容に応じて伸び、最大幅 400px でテキストが折り返される）
// 折り返し（word-break: break-word）は react-toastify のデフォルトスタイルに含まれているため指定しない
const baseToastStyle: CSSProperties = {
  borderRadius: '4px',
  fontSize: '16px',
  color: '#000000',
  minHeight: '56px',
  maxWidth: '400px',
  width: '100%',
  padding: '12px 16px',
};

export const showToast = (type: ToastType, message: string) => {
  if (type === 'success') {
    toast[type](message, {
      icon: (
        <img
          src="/agent/job_change_processes/icon-success.svg"
          alt="Success"
          width={20}
          height={20}
        />
      ),
      closeButton: CustomCloseButton,
      style: {
        ...baseToastStyle,
        backgroundColor: '#EDF8FF',
        border: '1px solid #005FFF',
      },
    });
  }
  if (type === 'error') {
    toast[type](message, {
      icon: (
        <img src="/agent/job_change_processes/icon-error.svg" alt="Error" width={20} height={20} />
      ),
      closeButton: CustomCloseButton,
      style: {
        ...baseToastStyle,
        backgroundColor: '#FFF0F0',
        border: '1px solid #D70000',
      },
    });
  }
  if (type === 'loading') {
    toast[type](message, {
      icon: <MaterialIcon name="progress_activity" className="animate-spin text-primary" />,
      closeButton: CustomCloseButton,
      style: {
        ...baseToastStyle,
        backgroundColor: '#EDF8FF',
        border: '1px solid #005FFF',
        marginRight: '4px',
      },
    });
  }
};

export const showActionToast = (
  message: string,
  actionLabel: string,
  onAction: () => void,
  autoCloseMs: number
) => {
  toast.success(
    ({ closeToast }) => (
      <div className="flex w-full items-center gap-sm">
        <span className="min-w-0 flex-1">{message}</span>
        <button
          type="button"
          className="mr-sm shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-primary underline"
          onClick={() => {
            closeToast();
            onAction();
          }}
        >
          {actionLabel}
        </button>
      </div>
    ),
    {
      autoClose: autoCloseMs,
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      icon: (
        <img
          src="/agent/job_change_processes/icon-success.svg"
          alt="Success"
          width={20}
          height={20}
        />
      ),
      closeButton: CustomCloseButton,
      style: {
        ...baseToastStyle,
        backgroundColor: '#EDF8FF',
        border: '1px solid #005FFF',
      },
    }
  );
};

export const showSuccessToast = (message: string) => showToast('success', message);
export const showErrorToast = (message: string) => showToast('error', message);
export const showLoadingToast = (message: string) => showToast('loading', message);

export const ToastProvider = () => (
  <ToastContainer
    position="top-right"
    autoClose={5000}
    hideProgressBar
    rtl={false}
    pauseOnHover
    theme="colored"
    // 複数行表示に対応するため、コンテナ幅は画面に追従しつつ最大 400px まで許容する
    style={{ zIndex: 99999, maxWidth: '400px', width: '100%' }}
  />
);
